import { FC } from 'react'

import { useForm, useStore } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { getRouteApi, useRouter } from '@tanstack/react-router'
import { Request } from 'commons/utils/request/request'
import {
  Dialog,
  DialogFace,
  DialogFooterActions,
  FF,
  LeaveConfirmation,
  TextareaInput,
  Toast,
  handleFormSubmissionError
} from 'ui/components'
import { AdverseMediaDetailsType } from '~bank-adverse-media/types'

import { s } from './styles'

type Props = {
  onClose: () => void
}

export const EditNotesDialog: FC<Props> = ({ onClose }) => {
  const router = useRouter()

  const routeApi = getRouteApi('/_app/adverse-media/$id')
  const loaderData = routeApi.useLoaderData({ select: (s) => s }) as AdverseMediaDetailsType

  const { mutateAsync: changeNotes } = useMutation<void, Error, { id: number; notes: string }>({
    mutationFn: (opts) => Request.put(Request.urls.bank.adverseMediaCaseChangeNotes, opts),
    onSuccess: async () => {
      Toast.successSmall('Notes have been saved.')
      onClose()
      await router.invalidate()
    },
    onError: (e) => {
      handleFormSubmissionError(e, form)
    }
  })

  const form = useForm({
    defaultValues: { notes: loaderData.notes },
    onSubmit: async ({ value }) => {
      await changeNotes({
        id: loaderData.id,
        notes: value.notes
      })
    }
  })

  return (
    <LeaveConfirmation preventLeaving={useStore(form.store, (s) => s.isDirty && !s.isSubmitting && !s.isSubmitted)}>
      <Dialog
        face={DialogFace.PRIMARY}
        id="edit-notes-dialog"
        onClose={onClose}
        title={loaderData.title}
        subTitle="Edit Notes"
        onSubmit={form.handleSubmit}
        footerActions={[DialogFooterActions.SAVE, DialogFooterActions.CANCEL]}
      >
        <s.Content>
          <form.Field
            name="notes"
            children={(notesField) => (
              <FF
                field={notesField}
                label="Optional Notes"
                input={(inputProps) => <TextareaInput maxLength={200} {...inputProps} />}
              />
            )}
          />
        </s.Content>
      </Dialog>
    </LeaveConfirmation>
  )
}
